import { Module, Mutation, Action } from "vuex-class-modules"
import { apiService } from "@/services/api.service"
import { Endpoint } from "@/services/endpoints"
import { ServerResponse } from "@/models/ServerResponse"
import { store } from "../store"
import { Modules } from "../modules"
import { ToDo, ToDoMetrics } from "@/models/ToDo"
import { BaseModule } from "@/models/BaseModule"
import { FilterToDo, FilterToDoCount } from "@/models/Filter/FilterToDo"
import { AlertActions, alertModule } from "./alert.module"
import { AlertContentItem, AlertType } from "@/models/Alert"
import { $t } from "@/plugins/i18n"
import { accountModule } from "./account.module"
import { UserActions, userModule } from "./user.module"
import { User } from "@/models/User"
import { parsingServerResponseContent } from "@/utils/helpers"
import { UpdateObjectValue } from "@/models"

export enum ToDoActions {
  GET_TODOS = "GET_TODOS",
  GET_TODOS_SERVER_RESPONSE = "GET_TODOS_SERVER_RESPONSE",
  ADD_TODO = "ADD_TODO",
  GET_TODO = "GET_TODO",
  UPDATE_TODO_VALUE = "UPDATE_TODO_VALUE",
  RESET_TO_DO = "RESET_TO_DO",
  RESET_TO_DOS = "RESET_TO_DOS",
  TODO_ITEM_RESOLVE = "TODO_ITEM_RESOLVE",
  TODO_ITEM_UNRESOLVE = "TODO_ITEM_UNRESOLVE",
  TODO_ITEM_DELETE_ATTACHMENT = "TODO_ITEM_DELETE_ATTACHMENT",
  REMOVE_TODO_FROM_LIST = "REMOVE_TODO_FROM_LIST",
  SET_TODO_IN_LIST = "SET_TODO_IN_LIST",
  SET_TODO = "SET_TODO",
  UPDATE_TODO = "UPDATE_TODO",
  DELETE_TODO = "DELETE_TODO",
  GET_OPENED_TODO_COUNT = "GET_OPENED_TODO_COUNT",
  GET_TODO_METRICS = "GET_TODO_METRICS",
  SET_OPENED_TODO_COUNT_FROM_SSE = "SET_OPENED_TODO_COUNT_FROM_SSE",
}

@Module
class ToDoModule extends BaseModule {
  toDos: ToDo[] = []
  toDo: ToDo = new ToDo()
  toDosServerResponse: ServerResponse<ToDo>
  openedToDoCount = 0

  get accountId() {
    return accountModule.getCurrentAccountId()
  }

  get profileId() {
    const profile: User = userModule[`get/${UserActions.GET_USER_CURRENT_PROFILE}`]

    return profile.id
  }

  get [`get/${ToDoActions.GET_TODO}`]() {
    return this.toDo
  }

  get [`get/${ToDoActions.GET_TODOS}`]() {
    return this.toDos
  }

  get [`get/${ToDoActions.GET_TODOS_SERVER_RESPONSE}`]() {
    return this.toDosServerResponse
  }

  get [`get/${ToDoActions.GET_OPENED_TODO_COUNT}`]() {
    return this.openedToDoCount
  }

  @Mutation
  setToDos(serverResponse: ServerResponse<ToDo>) {
    this.toDos = parsingServerResponseContent({
      originalContent: this.toDos,
      serverResponseContent: serverResponse.content,
      numberOfPage: serverResponse.pageable.pageNumber,
    })
    this.toDosServerResponse = { ...serverResponse, content: this.toDos }
  }

  @Mutation
  resetToDos() {
    this.toDos = []
  }

  @Mutation
  onUpdateToDoValue(payload: UpdateObjectValue<ToDo>) {
    Object.assign(this.toDo, { [payload["id"]]: payload["value"] })
  }

  @Mutation
  setToDo(toDo: ToDo) {
    this.toDo = new ToDo(toDo)
  }

  @Mutation
  setToDoInList(toDo: ToDo) {
    const index = this.toDos.findIndex(item => item.id === toDo.id)
    if (index === -1) {
      this.toDos.unshift(toDo)
    } else {
      this.toDos[index] = toDo
    }
  }

  @Mutation
  removeToDoFromList(toDoItemId: string) {
    this.toDos = this.toDos.filter(toDo => toDo.id !== toDoItemId)
  }

  @Mutation
  setOpenedToDoCount(count: number) {
    this.openedToDoCount = count
  }

  @Action
  async [ToDoActions.GET_TODOS](filterToDo : FilterToDo) {
    const filter = filterToDo.getJsonObj()

    const data = await apiService.get<ServerResponse<ToDo>>(Endpoint.TODO(this.accountId), filter)
    return this.handleResponse<ServerResponse<ToDo>>(data, (data) => {
      const content: ToDo[] = data.content.map(toDo => new ToDo(toDo))
      data = { ...data, content }

      this.setToDos(data)
      return data
    })
  }

  /** Returns the created ToDo while the filtered list is updated by its SSE event. */
  @Action
  async [ToDoActions.ADD_TODO](toDo: ToDo) {
    const data = await apiService.post<ToDo>(Endpoint.TODO(this.accountId), toDo.getJsonObj())
    return this.handleResponse<ToDo>(data, (data) => {
      const alertContent: AlertContentItem[] = [{
        text: `ToDo ${$t("alert.created")}`,
        type: "regular",
      }]
      alertModule[AlertActions.SHOW_ALERT]({
        content: alertContent,
        type: AlertType.SUCCESS,
        theme: "toast",
      })
      return new ToDo(data)
    })
  }

  @Action
  async [ToDoActions.TODO_ITEM_RESOLVE](todoItemId: string) {
    const data = await apiService.patch<ToDo>(Endpoint.TODO_ITEM_RESOLVE(this.accountId, todoItemId))
    return this.handleResponse<ToDo>(data, data => new ToDo(data))
  }

  @Action
  async [ToDoActions.TODO_ITEM_UNRESOLVE](todoItemId: string) {
    const data = await apiService.patch<ToDo>(Endpoint.TODO_ITEM_UNRESOLVE(this.accountId, todoItemId))
    return this.handleResponse<ToDo>(data, data => new ToDo(data))
  }

  @Action
  async [ToDoActions.TODO_ITEM_DELETE_ATTACHMENT]({ todoItemId, fileMetadataId }: {
    todoItemId: string
    fileMetadataId: string
  }) {
    const data = await apiService.delete<void>(
      Endpoint.TODO_ITEM_DELETE_ATTACHMENT(this.accountId, todoItemId, fileMetadataId)
    )
    return this.handleResponse<void>(data)
  }

  /** Returns the updated ToDo while the filtered list is updated by its SSE event. */
  @Action
  async [ToDoActions.UPDATE_TODO](toDo: ToDo) {
    const data = await apiService.put<ToDo>(
      Endpoint.TODO_ITEM(this.accountId, toDo.id),
      toDo.getUpdateJsonObj()
    )
    return this.handleResponse<ToDo>(data, (data) => {
      const todo = new ToDo(data)
      const alertContent: AlertContentItem[] = [
        {
          text: "ToDo ",
          type: "regular",
        },
        {
          text: `${todo.name} `,
          type: "bold",
        },
        {
          text: $t("alert.updated"),
          type: "regular",
        },
      ]
      alertModule[AlertActions.SHOW_ALERT]({
        content: alertContent,
        type: AlertType.SUCCESS,
        theme: "toast",
      })
      return todo
    })
  }

  /** Permanently removes a ToDo; the SSE event owns removal from the visible list. */
  @Action
  async [ToDoActions.DELETE_TODO](todoItemId: string) {
    const data = await apiService.delete<void>(Endpoint.TODO_ITEM(this.accountId, todoItemId))
    return this.handleResponse<void>(data)
  }

  @Action
  async [ToDoActions.GET_TODO]({ toDoId, withoutUpdate }: { toDoId: string, withoutUpdate?: boolean }) {
    const data = await apiService.get<ToDo>(Endpoint.TODO_ITEM(this.accountId, toDoId))
    return this.handleResponse<ToDo>(data, (data) => {
      const todo = new ToDo(data)
      if(!withoutUpdate) {
        this.setToDo(todo)
      }
      return todo
    })
  }

  @Action
  async [ToDoActions.GET_OPENED_TODO_COUNT]() {
    const filter = new FilterToDoCount({
      status: "ACTIVE",
      completedByMe: false,
    })
    const data = await apiService.get<number>(
      Endpoint.TODO_COUNT(this.accountId),
      filter.getJsonObj()
    )
    return this.handleResponse<number>(data, (data) => {
      this.setOpenedToDoCount(data)
      // This is a hack to avoid getting true if data is 0
      return `${data}`
    })
  }

  /** Loads metrics for the same ToDo filters as the list without sending pagination parameters. */
  @Action
  async [ToDoActions.GET_TODO_METRICS](filterToDo: FilterToDo) {
    const filter = filterToDo.getJsonObj({ withoutPagination: true })
    const data = await apiService.get<ToDoMetrics>(Endpoint.TODO_METRICS(this.accountId), filter)
    return this.handleResponse<ToDoMetrics>(data, metrics => metrics)
  }

  @Action
  [ToDoActions.UPDATE_TODO_VALUE](payload: UpdateObjectValue<ToDo>) {
    this.onUpdateToDoValue(payload)
  }

  @Action
  [ToDoActions.RESET_TO_DO]() {
    this.setToDo(new ToDo())
  }

  @Action
  [ToDoActions.RESET_TO_DOS]() {
    this.resetToDos()
  }

  @Action
  [ToDoActions.REMOVE_TODO_FROM_LIST](toDoItemId: string) {
    this.removeToDoFromList(toDoItemId)
  }

  @Action
  [ToDoActions.SET_TODO_IN_LIST](toDo: ToDo) {
    this.setToDoInList(toDo)
  }

  @Action
  [ToDoActions.SET_TODO](toDo: ToDo) {
    this.setToDo(toDo)
  }

  @Action
  [ToDoActions.SET_OPENED_TODO_COUNT_FROM_SSE](count: number) {
    this.setOpenedToDoCount(count)
  }
}

export const toDoModule = new ToDoModule({ store, name: Modules.TODO })
